// KITS-CAMPO (2026-06-16) — Kits de equipamento de campo.
// O patrimônio guarda itens soltos (coletora, GNSS base/rover...). Aqui o
// escritório monta "kits" (itens reais do patrimônio + acessórios livres) que
// vão a campo como uma unidade, com um QR Code pra check-in/check-out no PWA
// (fase 2) e alocação nas campanhas do Planejamento de Campo (fase 3).
//
// Histórico de saídas e voltas (06/10/2026, pedido do Lucas): visão
// "Histórico" com todos os check-ins do período e o botão por kit — quem levou,
// para qual contrato, quando saiu, quando voltou, quantos dias ficou fora.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, App as AntApp, Button, DatePicker, Drawer, Empty, Flex, Form, Input, InputNumber, Modal,
  Segmented, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, QrcodeOutlined, EditOutlined, PrinterOutlined,
  DownloadOutlined, ToolOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import {
  RegistroKit, emDestaque, textoDias, dataHoraSP, opcoesDosFiltros, filtrar, resumoDoHistorico, DIAS_ALERTA_ABERTO,
} from '../utils/kitsHistorico';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_CFG: Record<string, { label: string; cor: string }> = {
  disponivel: { label: 'Disponível', cor: 'green' },
  em_campo:   { label: 'Em campo',   cor: 'blue' },
  manutencao: { label: 'Manutenção', cor: 'gold' },
  inativo:    { label: 'Inativo',    cor: 'default' },
};

interface KitItem { patrimonio_id: string; descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null; }
interface Acessorio { nome: string; qtd: number; }
interface Kit {
  id: string;
  nome: string;
  codigo: string | null;
  descricao: string | null;
  status: string;
  itens: KitItem[];
  acessorios: Acessorio[];
  observacoes: string | null;
  token_hash: string | null;
  // quem está com o kit agora (check-in aberto) — vem do backend
  em_campo_nome?: string | null;
  em_campo_email?: string | null;
  em_campo_desde?: string | null;
  em_campo_contrato?: string | null;
}

function fmtDM(iso?: string | null): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); }
  catch { return ''; }
}
interface Patrimonio { id: string; descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null; }

// Conteúdo gravado no QR. O leitor do PWA reconhece o prefixo e ignora QRs alheios.
const qrValor = (hash: string) => `HDKIT:${hash}`;
const itemRotulo = (p: { descricao: string; modelo?: string | null; codigo_patrimonio?: string | null }) =>
  `${p.descricao}${p.modelo ? ` ${p.modelo}` : ''}${p.codigo_patrimonio ? ` [${p.codigo_patrimonio}]` : ''}`;

async function api(action: string, extra: any = {}) {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...extra }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export default function KitsCampo() {
  return <AntApp><KitsCampoInner /></AntApp>;
}

function KitsCampoInner() {
  const { message, modal } = AntApp.useApp();
  const [kits, setKits] = useState<Kit[]>([]);
  const [loading, setLoading] = useState(true);
  const [patrimonio, setPatrimonio] = useState<Patrimonio[]>([]);
  const [editando, setEditando] = useState<Kit | null>(null);
  const [aberto, setAberto] = useState(false);
  const [qrKit, setQrKit] = useState<Kit | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('ativos');
  const [busca, setBusca] = useState('');
  const [falhou, setFalhou] = useState(false);
  const [visao, setVisao] = useState<'kits' | 'historico'>('kits');
  const [kitDoHistorico, setKitDoHistorico] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_kit_listar');
      setKits(Array.isArray(d) ? d : []);
      setFalhou(false);
    } catch (e: any) {
      // falha ao carregar não pode virar "Nenhum kit"
      setFalhou(true);
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      try { const d = await api('op_kit_patrimonio'); setPatrimonio(Array.isArray(d) ? d : []); }
      catch { /* picker opcional */ }
    })();
  }, []);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return kits.filter(k => {
      if (filtroStatus === 'ativos' && k.status === 'inativo') return false;
      if (['disponivel', 'em_campo', 'manutencao', 'inativo'].includes(filtroStatus) && k.status !== filtroStatus) return false;
      if (!q) return true;
      const alvo = `${k.nome} ${k.codigo || ''} ${k.em_campo_nome || ''} ${(k.itens || []).map(i => `${i.descricao} ${i.codigo_patrimonio || ''}`).join(' ')}`.toLowerCase();
      return alvo.includes(q);
    });
  }, [kits, filtroStatus, busca]);

  async function excluir(k: Kit) {
    try { await api('op_kit_excluir', { id: k.id }); message.success('Kit excluído.'); carregar(); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }

  const colunas = [
    {
      title: 'Kit', key: 'nome',
      render: (_: any, k: Kit) => (
        <div>
          <Tooltip title="Clique para editar">
            <Text strong style={{ color: '#020D7F', cursor: 'pointer' }}
              onClick={() => { setEditando(k); setAberto(true); }}>{k.nome}</Text>
          </Tooltip>
          {k.codigo && <Tag style={{ marginLeft: 6 }}>{k.codigo}</Tag>}
          {k.descricao && <div><Text type="secondary" style={{ fontSize: 12 }}>{k.descricao}</Text></div>}
        </div>
      ),
    },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_: any, k: Kit) => {
        const c = STATUS_CFG[k.status] || STATUS_CFG.disponivel;
        return <Tag color={c.cor}>{c.label}</Tag>;
      },
    },
    {
      title: 'Com quem', key: 'portador', width: 210,
      render: (_: any, k: Kit) => {
        if (k.status !== 'em_campo' || !k.em_campo_nome) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
          <div>
            <div><Text style={{ fontSize: 13 }}>👤 {k.em_campo_nome}</Text></div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {k.em_campo_desde ? `desde ${fmtDM(k.em_campo_desde)}` : ''}
              {k.em_campo_contrato ? ` · ${k.em_campo_contrato}` : ''}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Itens', key: 'itens', width: 220,
      render: (_: any, k: Kit) => {
        const its = k.itens || [];
        const ac = k.acessorios || [];
        if (!its.length && !ac.length) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Tooltip title={
            <div style={{ fontSize: 12 }}>
              {its.map((i, n) => <div key={n}>• {itemRotulo(i)}</div>)}
              {ac.map((a, n) => <div key={'a' + n} style={{ opacity: 0.85 }}>◦ {a.nome}{a.qtd > 1 ? ` ×${a.qtd}` : ''}</div>)}
            </div>
          }>
            <Text style={{ fontSize: 13 }}>
              {its.length} item(s){ac.length ? ` + ${ac.length} acessório(s)` : ''}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ações', key: 'acoes', width: 180, align: 'right' as const,
      render: (_: any, k: Kit) => (
        <Space size={2}>
          <Tooltip title="Histórico de saídas e voltas">
            <Button size="small" type="text" icon={<HistoryOutlined />}
              onClick={() => { setKitDoHistorico(k.id); setVisao('historico'); }} />
          </Tooltip>
          <Tooltip title="Ver/Imprimir QR">
            <Button size="small" type="text" icon={<QrcodeOutlined />} onClick={() => setQrKit(k)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditando(k); setAberto(true); }} />
          </Tooltip>
          <Tooltip title="Excluir">
            <Button size="small" type="text" danger icon={<DeleteOutlined />}
              onClick={() => modal.confirm({
                title: `Excluir o kit "${k.nome}"?`,
                content: 'Não apaga os itens do patrimônio, só desfaz o agrupamento. Kit que já foi a campo não pode ser excluído (o histórico de saídas e voltas fica guardado) — para tirar de uso, edite e marque como Inativo.',
                okText: 'Excluir', okButtonProps: { danger: true }, onOk: () => excluir(k),
              })} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 12 }}>
        <Space wrap align="center">
          <Title level={4} style={{ margin: 0 }}>📦 Kits de Equipamento</Title>
          <Segmented value={visao} onChange={(v) => { setVisao(v as 'kits' | 'historico'); if (v === 'kits') setKitDoHistorico(null); }}
            options={[{ value: 'kits', label: 'Kits' }, { value: 'historico', label: 'Histórico de saídas e voltas' }]} />
        </Space>
        {visao === 'kits' && <Space wrap>
          <Input.Search allowClear placeholder="Buscar kit ou item…" style={{ width: 220 }}
            value={busca} onChange={e => setBusca(e.target.value)} />
          <Select showSearch optionFilterProp="label" value={filtroStatus} onChange={setFiltroStatus} style={{ width: 150 }}
            options={[
              { value: 'ativos', label: 'Ativos' },
              { value: 'disponivel', label: 'Disponíveis' },
              { value: 'em_campo', label: 'Em campo' },
              { value: 'manutencao', label: 'Em manutenção' },
              { value: 'inativo', label: 'Inativos' },
            ]} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditando(null); setAberto(true); }}>Novo kit</Button>
        </Space>}
      </Flex>

      {visao === 'historico' ? (
        <HistoricoKits kits={kits} kitInicial={kitDoHistorico} />
      ) : (<>
      <Paragraph type="secondary" style={{ fontSize: 13, marginTop: -4 }}>
        Agrupe itens do patrimônio num kit que vai a campo como uma unidade. Cada kit
        ganha um QR Code pra a equipe fazer check-in/check-out pelo app.
      </Paragraph>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : falhou ? (
        <Alert type="error" showIcon message="Não foi possível carregar os kits."
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
      ) : filtrados.length === 0 ? (
        <Empty description="Nenhum kit. Clique em 'Novo kit' pra montar o primeiro." style={{ marginTop: 40 }} />
      ) : (
        <Table rowKey="id" size="small" dataSource={filtrados} columns={colunas} pagination={false} />
      )}
      </>)}

      <KitDrawer aberto={aberto} kit={editando} patrimonio={patrimonio}
        onFechar={() => setAberto(false)}
        onSalvo={() => { setAberto(false); carregar(); }} />

      <QrModal kit={qrKit} onFechar={() => setQrKit(null)} />
    </div>
  );
}

// ── Histórico de saídas e voltas ─────────────────────────────────────────────
// O servidor traz os check-ins do PERÍODO; kit, contrato, pessoa e texto
// filtram aqui, na hora (as listas dos filtros não encolhem ao filtrar).
const LIMITE_HISTORICO = 1000;
function HistoricoKits({ kits, kitInicial }: { kits: Kit[]; kitInicial: string | null }) {
  const hoje = dayjs();
  const [periodo, setPeriodo] = useState<[Dayjs, Dayjs]>([hoje.startOf('year'), hoje]);
  const [registros, setRegistros] = useState<RegistroKit[]>([]);
  const [cortado, setCortado] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState<string | null>(null);
  const [kit, setKit] = useState<string | null>(kitInicial);
  const [contrato, setContrato] = useState<string | null>(null);
  const [pessoa, setPessoa] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  useEffect(() => { setKit(kitInicial); }, [kitInicial]);
  const pedido = useRef(0);   // trocar o período rápido: vale só a resposta do último pedido

  const carregar = useCallback(async () => {
    const meu = ++pedido.current;
    setCarregando(true);
    try {
      const d = await api('op_kit_historico', { de: periodo[0].format('YYYY-MM-DD'), ate: periodo[1].format('YYYY-MM-DD'), limite: LIMITE_HISTORICO });
      if (meu !== pedido.current) return;
      if (!d || !Array.isArray(d.registros)) throw new Error((d && (d.error || d.erro)) || 'resposta inesperada');
      setRegistros(d.registros); setCortado(!!d.cortado); setFalhou(null);
    } catch (e: any) {
      // falha não vira "nenhuma saída no período"
      if (meu === pedido.current) setFalhou(e.response?.data?.error || e.message || 'erro');
    } finally { if (meu === pedido.current) setCarregando(false); }
  }, [periodo]);
  useEffect(() => { carregar(); }, [carregar]);

  const opcoes = useMemo(() => opcoesDosFiltros(registros), [registros]);
  const opcoesKit = useMemo(() => {
    const m = new Map(opcoes.kits.map(o => [o.value, o.label]));
    for (const k of kits) if (!m.has(k.id)) m.set(k.id, k.codigo ? `${k.nome} (${k.codigo})` : k.nome);
    return Array.from(m, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [opcoes, kits]);
  // contrato ou pessoa que não aparece no período novo sai do filtro (senão o campo mostraria um código cru)
  useEffect(() => {
    if (contrato && !opcoes.contratos.some(o => o.value === contrato)) setContrato(null);
    if (pessoa && !opcoes.pessoas.some(o => o.value === pessoa)) setPessoa(null);
  }, [opcoes, contrato, pessoa]);
  const lista = useMemo(() => filtrar(registros, { kit, contrato, pessoa, texto }), [registros, kit, contrato, pessoa, texto]);
  const resumo = useMemo(() => resumoDoHistorico(lista), [lista]);

  const colunas = [
    { title: 'Kit', key: 'kit', width: 190, render: (_: any, r: RegistroKit) => (
      <div><Text strong style={{ fontSize: 13 }}>{r.kit_nome}</Text>{r.kit_codigo && <Tag style={{ marginLeft: 6 }}>{r.kit_codigo}</Tag>}
        {r.itens_na_saida > 0 && <div><Text type="secondary" style={{ fontSize: 11 }}>{r.itens_na_saida} item(s) na saída</Text></div>}</div>) },
    { title: 'Saiu', key: 'saiu', width: 150, render: (_: any, r: RegistroKit) => <Text style={{ fontSize: 12 }}>{dataHoraSP(r.saiu_em)}</Text> },
    { title: 'Voltou', key: 'voltou', width: 150, render: (_: any, r: RegistroKit) => (r.aberto
      ? <Tag color={emDestaque(r) ? 'orange' : 'blue'}>em campo</Tag>
      : <Text style={{ fontSize: 12 }}>{dataHoraSP(r.voltou_em)}</Text>) },
    { title: 'Dias', key: 'dias', width: 140, render: (_: any, r: RegistroKit) => (emDestaque(r)
      ? <Tooltip title={`Fora há mais de ${DIAS_ALERTA_ABERTO} dias sem devolução — continua cobrando o contrato.`}><Text strong style={{ color: '#d46b08' }}>{textoDias(r.dias_em_campo)}</Text></Tooltip>
      : <Text>{textoDias(r.dias_em_campo)}{r.aberto ? ' até hoje' : ''}</Text>) },
    { title: 'Com quem', key: 'quem', width: 190, render: (_: any, r: RegistroKit) => (
      <div><Text style={{ fontSize: 13 }}>👤 {r.usuario_nome}</Text>
        {r.devolvido_por && r.devolvido_por.toLowerCase() !== r.usuario_email.toLowerCase() && (
          <div><Text type="secondary" style={{ fontSize: 11 }}>devolvido por {r.devolvido_por_nome || r.devolvido_por}</Text></div>)}</div>) },
    { title: 'Contrato', key: 'contrato', render: (_: any, r: RegistroKit) => (r.contrato_id
      ? <div><Text style={{ fontSize: 13 }}>{r.contrato_numero}</Text>{r.contrato_titulo && <Text type="secondary" style={{ fontSize: 12 }}> — {r.contrato_titulo}</Text>}
          {r.contrato_da_campanha && <div><Text type="secondary" style={{ fontSize: 11 }}>pela campanha {r.campanha_titulo || ''}</Text></div>}</div>
      : <Tooltip title="Sem contrato: o custo do equipamento fica na empresa."><Tag color="red">sem contrato</Tag></Tooltip>) },
    { title: 'Observações', key: 'obs', width: 230, render: (_: any, r: RegistroKit) => (
      <div style={{ fontSize: 12 }}>
        {r.observacao && <div><Text type="secondary">saída:</Text> {r.observacao}</div>}
        {r.observacao_saida && <div><Text type="secondary">volta:</Text> {r.observacao_saida}</div>}
        {!r.observacao && !r.observacao_saida && <Text type="secondary">—</Text>}
      </div>) },
  ];

  return (
    <div>
      <Flex wrap="wrap" gap={8} style={{ marginBottom: 10 }}>
        <DatePicker.RangePicker value={periodo} format="DD/MM/YYYY" allowClear={false}
          onChange={(v) => { if (v && v[0] && v[1]) setPeriodo([v[0], v[1]]); }}
          presets={[
            { label: 'Este ano', value: [hoje.startOf('year'), hoje] },
            { label: 'Últimos 90 dias', value: [hoje.subtract(90, 'day'), hoje] },
            { label: 'Este mês', value: [hoje.startOf('month'), hoje] },
            { label: 'Mês passado', value: [hoje.subtract(1, 'month').startOf('month'), hoje.subtract(1, 'month').endOf('month')] },
          ]} />
        <Select showSearch allowClear optionFilterProp="label" placeholder="Kit" style={{ width: 220 }}
          value={kit || undefined} onChange={(v) => setKit(v || null)} options={opcoesKit} />
        <Select showSearch allowClear optionFilterProp="label" placeholder="Contrato" style={{ width: 260 }}
          value={contrato || undefined} onChange={(v) => setContrato(v || null)} options={opcoes.contratos} />
        <Select showSearch allowClear optionFilterProp="label" placeholder="Pessoa (levou ou devolveu)" style={{ width: 220 }}
          value={pessoa || undefined} onChange={(v) => setPessoa(v || null)} options={opcoes.pessoas} />
        <Input.Search allowClear placeholder="Buscar nas observações, campanha…" style={{ width: 240 }}
          onChange={(e) => setTexto(e.target.value)} />
      </Flex>

      {carregando ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : falhou ? (
        <Alert type="error" showIcon message="Não foi possível carregar o histórico." description={falhou}
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
      ) : (<>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
          {resumo.saidas} saída(s) no período · {resumo.emCampo} em campo agora
          {resumo.destaque > 0 && <Text style={{ color: '#d46b08' }}> · {resumo.destaque} fora há mais de {DIAS_ALERTA_ABERTO} dias</Text>}
        </Text>
        {cortado && <Alert type="warning" showIcon style={{ marginBottom: 8 }}
          message={`Mostrando só as ${LIMITE_HISTORICO} saídas mais recentes do período — encurte o período para ver as anteriores.`} />}
        {lista.length === 0
          ? <Empty description="Nenhuma saída de kit com esses filtros no período." style={{ marginTop: 32 }} />
          : <Table rowKey="id" size="small" dataSource={lista} columns={colunas} scroll={{ x: 1100 }}
              pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
              onRow={(r) => ({ style: emDestaque(r) ? { background: '#fff7e6' } : undefined })} />}
      </>)}
    </div>
  );
}

// ── Drawer de criar/editar kit ───────────────────────────────────────────────
function KitDrawer({ aberto, kit, patrimonio, onFechar, onSalvo }: {
  aberto: boolean; kit: Kit | null; patrimonio: Patrimonio[];
  onFechar: () => void; onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    form.resetFields();
    if (kit) {
      form.setFieldsValue({
        nome: kit.nome,
        codigo: kit.codigo || undefined,
        descricao: kit.descricao || undefined,
        status: kit.status,
        itens_ids: (kit.itens || []).map(i => i.patrimonio_id),
        acessorios: kit.acessorios || [],
        observacoes: kit.observacoes || undefined,
      });
    } else {
      form.setFieldsValue({ status: 'disponivel', acessorios: [], itens_ids: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, kit?.id]);

  // Opções = patrimônio ativo + (no modo edição) itens do kit que já saíram do
  // patrimônio ativo, pra não sumirem da seleção.
  const itemOptions = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const p of patrimonio) mapa.set(p.id, itemRotulo(p));
    for (const i of (kit?.itens || [])) if (!mapa.has(i.patrimonio_id)) mapa.set(i.patrimonio_id, itemRotulo(i) + ' (baixado)');
    const catDe = (id: string) => patrimonio.find(p => p.id === id)?.categoria || 'Outros';
    const cats = Array.from(new Set(Array.from(mapa.keys()).map(catDe)));
    return cats.map(cat => ({
      label: cat,
      options: Array.from(mapa.entries()).filter(([id]) => catDe(id) === cat)
        .map(([id, label]) => ({ value: id, label })),
    }));
  }, [patrimonio, kit]);

  async function salvar(v: any) {
    setSalvando(true);
    try {
      const payload = {
        nome: v.nome,
        codigo: v.codigo || null,
        descricao: v.descricao || null,
        status: v.status || 'disponivel',
        itens_ids: v.itens_ids || [],
        acessorios: (v.acessorios || []).filter((a: any) => a && a.nome),
        observacoes: v.observacoes || null,
      };
      await api(kit ? 'op_kit_atualizar' : 'op_kit_criar', kit ? { id: kit.id, ...payload } : payload);
      message.success(kit ? 'Kit atualizado.' : 'Kit criado — QR gerado.');
      onSalvo();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Drawer open={aberto} onClose={onFechar} width={520} destroyOnClose
      title={kit ? '✏️ Editar kit' : 'Novo kit de equipamento'}>
      <Form form={form} layout="vertical" size="small" onFinish={salvar}>
        <Form.Item label="Nome do kit" name="nome" rules={[{ required: true, message: 'Dê um nome ao kit' }]}
          extra="Ex: Kit GNSS RTK 01">
          <Input placeholder="Kit GNSS RTK 01" />
        </Form.Item>
        <Flex gap={10}>
          <Form.Item label="Código" name="codigo" style={{ flex: 1 }} extra="Opcional — etiqueta interna">
            <Input placeholder="KIT-GNSS-01" />
          </Form.Item>
          <Form.Item label="Status" name="status" style={{ width: 170 }}>
            <Select showSearch optionFilterProp="label" options={Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
        </Flex>
        <Form.Item label="Descrição" name="descricao">
          <Input placeholder="Pra que serve este kit (opcional)" />
        </Form.Item>

        <Form.Item label="Itens do patrimônio" name="itens_ids"
          rules={[{ required: true, message: 'Escolha pelo menos um item' }]}
          extra="Itens reais do patrimônio (rastreio pelo nº de patrimônio)">
          <Select mode="multiple" showSearch optionFilterProp="label" allowClear
            placeholder="Buscar item do patrimônio…" options={itemOptions} maxTagCount="responsive" />
        </Form.Item>

        <Form.Item label="Acessórios (não-patrimoniados)" style={{ marginBottom: 8 }}
          extra="Cabos, bastão, trena, baterias… o que não tem nº de patrimônio">
          <Form.List name="acessorios">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Flex key={field.key} gap={6} align="start">
                    <ToolOutlined style={{ marginTop: 8, color: '#0930D0' }} />
                    <Form.Item name={[field.name, 'nome']} style={{ flex: 1, marginBottom: 0 }}>
                      <Input placeholder="Acessório (ex: Bastão de 2m)" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'qtd']} style={{ width: 70, marginBottom: 0 }} initialValue={1}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Button size="small" type="text" danger onClick={() => remove(field.name)}>×</Button>
                  </Flex>
                ))}
                <Button size="small" type="dashed" block onClick={() => add({ qtd: 1 })}>+ Adicionar acessório</Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="Observações" name="observacoes">
          <TextArea rows={2} placeholder="Cuidados, número de série, validade de calibração…" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={salvando} block>
          {kit ? 'Salvar' : 'Criar kit'}
        </Button>
      </Form>
    </Drawer>
  );
}

// ── Modal do QR (ver / imprimir / baixar) ────────────────────────────────────
function QrModal({ kit, onFechar }: { kit: Kit | null; onFechar: () => void }) {
  const { message } = AntApp.useApp();
  if (!kit) return null;

  function getCanvas(): HTMLCanvasElement | null {
    return document.querySelector<HTMLCanvasElement>('#kit-qr-box canvas');
  }
  function baixarPng() {
    const c = getCanvas(); if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `QR-${(kit!.codigo || kit!.nome).replace(/[^a-zA-Z0-9._-]+/g, '_')}.png`;
    a.click();
  }
  function imprimir() {
    const c = getCanvas(); if (!c) return;
    const img = c.toDataURL('image/png');
    const itens = (kit!.itens || []).map(i => `<li>${itemRotulo(i)}</li>`).join('');
    const ac = (kit!.acessorios || []).map(a => `<li>${a.nome}${a.qtd > 1 ? ` ×${a.qtd}` : ''}</li>`).join('');
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) { message.warning('Permita pop-ups pra imprimir.'); return; }
    w.document.write(`
      <html><head><title>QR ${kit!.nome}</title>
      <style>
        body{font-family:Arial,sans-serif;text-align:center;padding:24px;color:#111}
        h2{margin:4px 0;font-size:20px} .cod{color:#555;font-size:13px;margin-bottom:12px}
        img{width:240px;height:240px} ul{text-align:left;font-size:12px;display:inline-block;margin:12px auto 0}
        .marca{margin-top:18px;color:#888;font-size:11px}
      </style></head><body>
        <h2>${kit!.nome}</h2>
        <div class="cod">${kit!.codigo || ''}</div>
        <img src="${img}" />
        ${itens || ac ? `<ul>${itens}${ac}</ul>` : ''}
        <div class="marca">Hydrodata · check-in/out pelo app de campo</div>
        <script>window.onload=function(){window.print()}</script>
      </body></html>`);
    w.document.close();
  }

  return (
    <Modal open={!!kit} onCancel={onFechar} title={`QR — ${kit.nome}`} footer={null} width={360}>
      <div style={{ textAlign: 'center' }}>
        {kit.token_hash ? (
          <>
            <div id="kit-qr-box" style={{ display: 'inline-block', padding: 12, background: '#fff', border: '1px solid #eee', borderRadius: 8 }}>
              <QRCodeCanvas value={qrValor(kit.token_hash)} size={220} level="M" includeMargin />
            </div>
            {kit.codigo && <div style={{ marginTop: 8 }}><Tag>{kit.codigo}</Tag></div>}
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 10 }}>
              Imprima e cole na maleta. A equipe aponta a câmera do app pra fazer check-in/check-out.
            </Paragraph>
            <Space style={{ marginTop: 4 }}>
              <Button icon={<PrinterOutlined />} onClick={imprimir}>Imprimir</Button>
              <Button icon={<DownloadOutlined />} onClick={baixarPng}>Baixar PNG</Button>
            </Space>
          </>
        ) : (
          <Text type="secondary">Este kit ainda não tem QR. Edite e salve pra gerar.</Text>
        )}
      </div>
    </Modal>
  );
}
